import PropTypes from 'prop-types';
import { Button } from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';

import config from '@plone/volto/registry';
import { BlocksForm } from '@plone/volto/components/manage/Form';
import EditBlockWrapper from '@plone/volto/components/manage/Blocks/Container/EditBlockWrapper';

import {
  TAB_ALLOWED_BLOCKS,
  pickNestedBlocksConfig,
} from '../../../config/nestedBlocks';

const getNestedBlocksConfig = () =>
  config.blocks.blocksConfig.csjTabs?.blocksConfig ||
  pickNestedBlocksConfig(config.blocks.blocksConfig, TAB_ALLOWED_BLOCKS);

const columnContainsSelection = (columnBlock, selectedId) => {
  if (columnBlock?.['@type'] !== 'csjColumnGrid') {
    return false;
  }

  return ['left', 'right'].some((side) => {
    const blocks = columnBlock[`${side}_blocks`] || {};
    const items = columnBlock[`${side}_blocks_layout`]?.items || [];
    if (items.includes(selectedId)) {
      return true;
    }

    return items.some((id) => {
      const child = blocks[id];
      return (
        child?.['@type'] === 'csjIconCardGrid' &&
        (child.blocks_layout?.items || []).includes(selectedId)
      );
    });
  });
};

const resolveTabSelectedBlock = (blocks, layoutItems, selectedId) => {
  if (!selectedId) {
    return null;
  }
  if (layoutItems.includes(selectedId)) {
    return selectedId;
  }

  return (
    layoutItems.find((id) => columnContainsSelection(blocks[id], selectedId)) ||
    null
  );
};

const TabBlocksEditor = ({
  tab,
  tabIndex,
  data,
  block,
  onChangeBlock,
  pathname,
  selected,
  manage,
  metadata,
  properties,
  tabBlockSelected,
  setTabBlockSelected,
}) => {
  const tabBlocks = tab?.blocks || {};
  const tabLayout = tab?.blocks_layout || { items: [] };
  const blocksConfig = getNestedBlocksConfig();
  const selectedBlockInTab = resolveTabSelectedBlock(
    tabBlocks,
    tabLayout.items,
    tabBlockSelected,
  );

  const updateTab = (updatedTab) => {
    const tabs = [...(data.tabs || [])];
    tabs[tabIndex] = updatedTab;
    onChangeBlock(block, { ...data, tabs });
  };

  const onAddBlock = () => {
    const newBlockId = uuid();
    updateTab({
      ...tab,
      blocks: {
        ...tabBlocks,
        [newBlockId]: { '@type': 'slate' },
      },
      blocks_layout: {
        items: [...tabLayout.items, newBlockId],
      },
    });
    setTabBlockSelected(newBlockId);
  };

  return (
    <div className="csj-tabs-panel-editor">
      <div className="csj-tabs-panel-header">
        <span>{tab?.title || ''}</span>
        {selected ? (
          <Button compact size="mini" type="button" onClick={onAddBlock}>
            + Bloc
          </Button>
        ) : null}
      </div>
      <div
        className="csj-tabs-panel-blocks"
        role="presentation"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <BlocksForm
          metadata={metadata || properties}
          properties={{
            blocks: tabBlocks,
            blocks_layout: tabLayout,
          }}
          direction="vertical"
          manage={manage}
          selectedBlock={selected ? selectedBlockInTab : null}
          blocksConfig={blocksConfig}
          isContainer
          isMainForm={false}
          disableAddBlockOnEnterKey
          onSelectBlock={(id) => {
            setTabBlockSelected(id);
          }}
          onChangeFormData={(newFormData) => {
            updateTab({
              ...tab,
              blocks: newFormData.blocks,
              blocks_layout: newFormData.blocks_layout,
            });
          }}
          onChangeField={(id, value) => {
            if (id === 'blocks' || id === 'blocks_layout') {
              updateTab({
                ...tab,
                blocks: id === 'blocks' ? value : tabBlocks,
                blocks_layout: id === 'blocks_layout' ? value : tabLayout,
              });
            }
          }}
          pathname={pathname}
        >
          {({ draginfo }, editBlock, blockProps) => (
            <EditBlockWrapper draginfo={draginfo} blockProps={blockProps}>
              {editBlock}
            </EditBlockWrapper>
          )}
        </BlocksForm>
      </div>
    </div>
  );
};

TabBlocksEditor.propTypes = {
  tab: PropTypes.objectOf(PropTypes.any).isRequired,
  tabIndex: PropTypes.number.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  block: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  manage: PropTypes.bool.isRequired,
  metadata: PropTypes.objectOf(PropTypes.any),
  properties: PropTypes.objectOf(PropTypes.any),
  tabBlockSelected: PropTypes.string,
  setTabBlockSelected: PropTypes.func.isRequired,
};

export default TabBlocksEditor;
